import { useState } from 'react';
import { Search } from 'lucide-react';
import { Skeleton } from '@/design/components';
import { useProductSearch } from './api';

const FIELD = 'min-h-11 w-full rounded-sm border border-muted bg-surface px-3 text-body text-ink';

/**
 * בחירת מוצר אחד מתוך הקטלוג, בחיפוש.
 *
 * מחליף רשימת select של כל המוצרים. עם שלושה עשר מוצרי דמה
 * רשימה כזו נוחה; עם אלפיים מוצרים מקומקס היא גלילה של דקה,
 * ובדרך היא מורידה את כל הקטלוג לדפדפן.
 *
 * מציג עשר תוצאות בלבד: מי שמקבל חמישים תוצאות לא בחר נכון את
 * מילת החיפוש, והרשימה לא תעזור לו.
 */
export function ProductPicker({
  label,
  onPick,
}: {
  label: string;
  onPick: (product: { id: string; name: string }) => void;
}) {
  const [term, setTerm] = useState('');
  const found = useProductSearch({ term, categoryId: null, includeInactive: false, page: 0 });

  const rows = (found.data?.rows ?? []).slice(0, 10);

  return (
    <div className="grid gap-2">
      <div className="relative">
        <label htmlFor="product-pick" className="sr-only">
          {label}
        </label>
        {/* RTL: האייקון ב start, והריפוד נפתח מאותו צד */}
        <Search
          size={20}
          strokeWidth={1.75}
          aria-hidden="true"
          className="pointer-events-none absolute start-3 top-1/2 -translate-y-1/2 text-muted"
        />
        <input
          id="product-pick"
          type="search"
          autoFocus
          value={term}
          onChange={(event) => setTerm(event.target.value)}
          placeholder="חיפוש מוצר לפי שם או מק״ט"
          className={`${FIELD} ps-10`}
        />
      </div>

      {term.trim() === '' && (
        <p className="m-0 text-body-sm text-muted">הקלדה תביא את המוצרים המתאימים.</p>
      )}

      {term.trim() !== '' && found.isPending && <Skeleton className="h-10" />}

      {term.trim() !== '' && found.data?.total === 0 && (
        <p className="m-0 text-body-sm text-muted">אין תוצאה ל"{term}".</p>
      )}

      {rows.length > 0 && term.trim() !== '' && (
        <ul className="grid max-h-56 list-none gap-1 overflow-y-auto p-0">
          {rows.map((product) => (
            <li key={product.id}>
              <button
                type="button"
                onClick={() => onPick({ id: product.id, name: product.name })}
                className="min-h-11 w-full cursor-pointer rounded-sm border-0 bg-transparent px-3 text-start text-body text-ink hover:bg-olive-soft"
              >
                {product.name}
                {product.sku && <span className="text-body-sm text-muted"> · {product.sku}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
